import { useEffect, useMemo, useRef, useState } from "react";
import BackArrow from "../../components/BackArrow";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import TakePicture from "./TakePicture";
import {
  checkCatchMessage,
  checkSystemWebcam,
  compressMediaFiles,
  manageErrorLogs,
} from "../../helpers/commonFunction";
import { isDesktop } from "react-device-detect";
import { AppButton, CustomImageCapture } from "../../components";
import { messages } from "../../helpers/messages";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { routes } from "../../helpers/constants";
import { insertRestaurantDetailsInDB } from "../Company/DBQuery";
import { reqToGetRestaurantDetailsFromIDB } from "../../store/slice/indexedDBSlice";

const SiphoningStep = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const {
    type,
    showUpload,
    selectedItem,
    selectedSubItem,
    selectedLocation,
    selectedRestaurant,
  } = location.state || {};

  const { restaurantList, isRestaurantListIDBChecked } = useSelector(
    (state) => ({
      restaurantList: state.indexedDBReducer?.restaurantList,
      isRestaurantListIDBChecked:
        state.indexedDBReducer?.isRestaurantListIDBChecked,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (!isRestaurantListIDBChecked) {
      dispatch(reqToGetRestaurantDetailsFromIDB({}));
    }
  }, [restaurantList, isRestaurantListIDBChecked]);

  // CURRENT SELECTED RESTAURANT
  const currSRestaurant = useMemo(() => {
    if (!Array.isArray(restaurantList) && restaurantList?.length === 0) {
      return selectedRestaurant;
    }
    const sRestaurant = restaurantList.find(
      (item) => item.id === selectedRestaurant.id
    );
    return sRestaurant || selectedRestaurant;
  }, [restaurantList]);

  // CURRENT SELECTED ITEM
  const currSItem = useMemo(() => {
    if (!currSRestaurant || currSRestaurant?.location?.length === 0) {
      return selectedItem;
    }

    const currResItem = currSRestaurant?.location.find(
      (loc) => loc.name === selectedLocation
    );
    if (!currResItem || currResItem?.items?.length === 0) return selectedItem;

    const currResSItem = currResItem?.items.find(
      (sub) => sub.id === selectedItem.id
    );
    return currResSItem || selectedItem;
  }, [currSRestaurant]);

  // CURRENT SELECTED SUB ITEM
  const currSubItem = useMemo(() => {
    if (!currSItem || currSItem?.sub_items?.length === 0)
      return selectedSubItem;

    const currSubItem = currSItem.sub_items.find(
      (sItem) => sItem.id === selectedSubItem.id
    );

    return currSubItem || selectedSubItem;
  }, [currSItem]);

  const inputRef = useRef(null);
  const webcamRef = useRef(null);
  const [selfieData, setSelfieData] = useState(null);
  const [openTakePicture, setOpenTakePicture] = useState(false);
  const { stage } = useParams();
  const [previousReports, setPreviousReports] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const uploadRef = useRef(null);

  useEffect(() => {
    if (!selectedItem || !selectedItem?.id) {
      navigate(routes.dashboard);
    }
  }, []);

  const getCurrentReportStep = (itemReport, allReportSteps) => {
    if (!Array.isArray(itemReport) || itemReport.length === 0) {
      return allReportSteps[0];
    }

    let lastCompletedIndex = -1;
    allReportSteps.forEach((step, index) => {
      if (itemReport.some((x) => x.status === step.status)) {
        lastCompletedIndex = index;
      }
    });
    return (
      allReportSteps[lastCompletedIndex + 1] ||
      allReportSteps[lastCompletedIndex]
    );
  };

  const allReportSteps = [
    {
      title: `1. Please Take Picture of the ${currSItem.name} before going to start the ${currSubItem?.name}`,
      status: "before",
      label: "Before",
    },
    {
      title: `2. Please Take Picture of the ${currSItem.name} while ${currSubItem?.name} is in process.`,
      status: "processing",
      label: "Processing",
    },
    {
      title: `3. Please Take Picture of the ${currSItem.name} after ${currSubItem?.name}.`,
      status: "after",
      label: "After",
    },
  ];

  useEffect(() => {
    setPreviousReports([...currSubItem.item_report]);
    const currentReportStep = getCurrentReportStep(
      currSubItem?.item_report,
      allReportSteps
    );
    setCurrentStep(currentReportStep);
  }, [currSubItem.item_report]);

  useEffect(() => {
    const currentReportStep = getCurrentReportStep(
      previousReports,
      allReportSteps
    );
    setCurrentStep(currentReportStep);
  }, [previousReports]);

  const openSelfieModal = async () => {
    await checkSystemWebcam()
      .then(() => {
        if (isDesktop) {
        } else {
          if (inputRef && inputRef.current) {
            inputRef.current.value = "";
          }
          inputRef.current.click();
        }
      })
      .catch(() => { });
  };

  const handleOpenUpload = () => {
    if (uploadRef && uploadRef.current) {
      uploadRef.current.value = "";
    }
    uploadRef.current.click();   // clicks the upload-only input
  };

  const handleGetSelfie = async (event) => {
    try {
      if (isDesktop) {
        const imageSrc = webcamRef?.current?.getScreenshot();
        if (!imageSrc) {
          throw new Error(messages.errors.captureImg);
        }
        setSelfieData(imageSrc);
      } else {
        const files = event?.target?.files;
        if (!files || (Array.isArray(files) && files?.length === 0)) {
          throw new Error(messages.errors.captureImg);
        }
        const base64 = await compressMediaFiles(files[0], "file", "selfie");
        setSelfieData(base64);
        setOpenTakePicture(true);
      }
    } catch (error) {
      manageErrorLogs(error);
      checkCatchMessage(error?.message || messages.errors.capture_selfie);
    }
  };

  const handleSubmit = async () => {
    try {
      const reqObj = {
        sub_item_id: currSubItem?.id,
        user_id: currSRestaurant?.id,
        image: null,
        base64: selfieData,
        record_date: new Date(),
        status: currentStep?.status,
      };

      const reqObjFinal = {
        reqObj: reqObj,
        currSItem: currSItem,
        currSubItem: currSubItem,
        currSRestaurant: currSRestaurant,
        selectedLocation: selectedLocation,
      };

      await insertRestaurantDetailsInDB(reqObjFinal, dispatch, type);
      setSelfieData(null);
      setOpenTakePicture(false);
      setPreviousReports((prev) => [...prev, reqObj]);

      if (currentStep?.status === "after") {
        navigate(routes.companyReportStages, { state: { ...location.state } });
      }
    } catch (error) { }
  };

  return (
    <div className="tcl-raffles-makati-page tcl-siphoning-step-details-page">
      {!openTakePicture && (
        <div className="tcl-raffles-makati-wrapper">
          <BackArrow
            handleClick={() =>
              navigate(routes.companyReportStages, {
                state: { ...location.state },
              })
            }
          />
          <button className="app-btn btn btn-green">{currSubItem?.name}</button>
          <div className="tcl-siphoning-step-content">
            <h6>Steps</h6>
            <p>{currentStep?.title}</p>
          </div>
          <div className="tap-to-start" onClick={() => openSelfieModal()}>
            Tap to <br /> TAKE PICTURE
          </div>

          {showUpload && (
            <AppButton
              title="UPLOAD"
              isBtnGreen={false}
              onClick={() => handleOpenUpload()}
            />
          )}
        </div>
      )}

      <input
        ref={uploadRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleGetSelfie}
      />
      {openTakePicture && (
        <TakePicture
          openSelfieModal={openSelfieModal}
          handleSubmit={handleSubmit}
          currentStep={currentStep}
          stage={stage}
          selfieData={selfieData}
          setSelfieData={setSelfieData}
          setOpenTakePicture={setOpenTakePicture}
        />
      )}

      <CustomImageCapture
        inputRef={inputRef}
        handleFileChange={handleGetSelfie}
      />
    </div>
  );
};

export default SiphoningStep;
